/**
 * AgendaView — vista lista/agenda del mese per viewport mobile (< md).
 *
 * La grid 8-colonne di MeseView è illeggibile su smartphone: qui gli stessi
 * dati (data.weeks[].giorni[]) sono resi come lista verticale di giorni del
 * mese, con MIT/azioni togglabili, badge scadenze e quick-add per giorno.
 * I giorni senza contenuto sono compressi in una riga sottile.
 */
import { Link } from 'react-router-dom';
import { AlertTriangle, Plus } from 'lucide-react';
import Checkbox from '../nav/Checkbox';

const GIORNI_IT = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

function weekdayShort(iso) {
  try {
    const d = new Date(iso + 'T00:00:00');
    const idx = (d.getDay() || 7) - 1;
    return GIORNI_IT[idx];
  } catch {
    return '';
  }
}

export default function AgendaView({ data, todayIso, onToggleMit, onToggleAzione }) {
  // Appiattisci le settimane nei soli giorni del mese corrente
  const giorni = (data.weeks || []).flatMap((w) => w.giorni).filter((g) => g.in_mese);

  return (
    <div className="divide-y divide-linesoft border-t border-b border-line" data-testid="cal-agenda">
      {giorni.map((g) => {
        const isToday = g.data === todayIso;
        const mitList = g.mit || [];
        const azList = g.azioni || [];
        const scad = g.scadenze || [];
        const hasContent = mitList.length > 0 || azList.length > 0 || scad.length > 0;

        return (
          <div
            key={g.data}
            className={'py-2 px-1 ' + (isToday ? 'bg-tint' : '')}
            data-testid={`agenda-day-${g.data}`}
          >
            <div className="flex items-center gap-2">
              <Link
                to={`/periodo/giorno/${g.data}`}
                className={
                  'font-mono text-xs tabular-nums shrink-0 w-14 ' +
                  (isToday ? 'bg-ink text-canvas px-1.5 py-0.5' : 'text-soft')
                }
                data-testid={`agenda-day-link-${g.data}`}
              >
                {weekdayShort(g.data)} {g.giorno}
              </Link>
              {scad.length > 0 && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.15em] text-accent"
                  title={scad.map((s) => s.titolo).join(' · ')}
                >
                  <AlertTriangle size={11} strokeWidth={1.5} /> {scad.length}
                </span>
              )}
              {!hasContent && <span className="flex-1 border-b border-dashed border-linesoft" aria-hidden="true" />}
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent('qp:quick-add-open', {
                  detail: { kind: 'mit', dataIso: g.data },
                }))}
                className="ml-auto p-1 text-mute hover:text-ink border border-transparent hover:border-line shrink-0"
                aria-label={`Aggiungi MIT o azione · ${g.data}`}
                data-testid={`agenda-day-add-${g.data}`}
              >
                <Plus size={13} strokeWidth={2} />
              </button>
            </div>

            {hasContent && (
              <ul className="mt-1.5 space-y-1.5 pl-14">
                {mitList.map((m) => (
                  <li key={m.id} className="flex items-start gap-2" data-testid={`agenda-mit-${m.id}`}>
                    <Checkbox
                      checked={m.fatto}
                      onToggle={() => onToggleMit(m.id, m.fatto)}
                      size={18}
                      label={m.testo}
                      testid={`agenda-toggle-mit-${m.id}`}
                    />
                    <span className="font-mono text-[9px] uppercase tracking-[0.12em] border border-ink px-1 mt-0.5 shrink-0">MIT</span>
                    <span
                      className="font-body text-sm leading-snug"
                      style={{
                        textDecoration: m.fatto ? 'line-through' : 'none',
                        color: m.fatto ? 'var(--qp-text-mute)' : undefined,
                      }}
                    >
                      {m.testo}
                    </span>
                  </li>
                ))}
                {azList.map((a) => (
                  <li key={a.id} className="flex items-start gap-2" data-testid={`agenda-azione-${a.id}`}>
                    <Checkbox
                      checked={a.fatto}
                      onToggle={() => onToggleAzione(a.id, a.fatto)}
                      size={18}
                      label={a.testo}
                      testid={`agenda-toggle-azione-${a.id}`}
                    />
                    <span className="font-mono text-[9px] uppercase tracking-[0.12em] border border-line text-soft px-1 mt-0.5 shrink-0">AZ</span>
                    <span
                      className="font-body text-sm leading-snug"
                      style={{
                        textDecoration: a.fatto ? 'line-through' : 'none',
                        color: a.fatto ? 'var(--qp-text-mute)' : undefined,
                      }}
                    >
                      {a.testo}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
